import type {UrlStateManagerService} from '../commons/services/url-state-manager.service';
import type {IHttpService, ILogService, IScope} from 'angular';
import KpValueEditor from '../commons/value-editors/kp-value-editor/KpValueEditor.svelte';
import type {Subscription} from 'rxjs';
import {getErrorMessage, responseToData, transferify} from 'shared/utils/data-service-utils';
import type {ModelInputChangeEvent} from '../commons/value-editors/events';
import type {LocalizationService} from '../commons/providers/localization.provider';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {Auth, EditedProperty} from 'typings/portaro.be.types';
import {prepareFormFields, updateEditorsIsDisabledProperty, updateEditorsModelProperty} from './utils';

declare global {
    interface Window {
        model: any & {
            view: {
                text: null,
                fields: EditedProperty[],
                submitPath: string,
                pageClass: string,
            };
        }
    }
}

export class PublicStatsController {
    public static controllerName = 'PublicStatsCtrl';

    public kpValueEditorComponent = KpValueEditor;
    public params: Record<string, any>;
    public stats: Record<string, any>;
    public charts: Record<string, GoogleChart>;
    public formFields: EditedProperty[];

    private originalFieldsSettings: EditedProperty[];
    private isLoading = false;
    private subscription: Subscription;

    /*@ngInject*/
    constructor(private urlStateManagerService: UrlStateManagerService,
                private localizationService: LocalizationService,
                private $http: IHttpService,
                private $scope: IScope,
                private $log: ILogService,
                public currentAuth: Auth) {
    }

    public init() {
        this.originalFieldsSettings = window.model.view.fields;

        this.createCharts();

        this.urlStateManagerService.register((params) => this.onStateChange(params));

        this.formFields = prepareFormFields(this.originalFieldsSettings);

        this.subscription = this.urlStateManagerService.isLoading$().subscribe((isLoading) => {
            this.isLoading = isLoading;
            this.formFields = updateEditorsIsDisabledProperty(this.formFields, isLoading);
        });

        this.$scope.$on('$destroy', () => {
            this.subscription.unsubscribe();
        });
    }

    public onChange(event: ModelInputChangeEvent<any>, fieldName: string) {
        this.params[fieldName] = event.detail;
        this.formFields = updateEditorsModelProperty(this.formFields, this.params);
    }

    @ngAsync()
    public onSubmit(): Promise<void> {
        return this.urlStateManagerService.requestChangeState(transferify(this.params));
    }

    @ngAsync()
    private onStateChange(params): Promise<void> {
        params = transferify(params);
        return Promise.resolve(this.$http.get('/api/stats/public', {params}))
            .then(responseToData)
            .then((data: any) => {
                this.stats = data.result ;
                this.params = data.params;
                this.formFields = updateEditorsModelProperty(this.formFields, this.params);
            })
            .catch((error) => {
                this.stats = null;
                this.$log.error(getErrorMessage(error));
            })
            .finally(() => this.refreshCharts());

    }

    private refreshCharts() {
        this.charts.searchTypesCounts.setNewData(this.stats.searchTypesCounts.counts);
        this.charts.movementsCounts.setNewData(this.stats.movementsCounts.counts);
        this.charts.entersAndLoginsCounts.setNewData(this.stats.entersAndLoginsCounts.counts);
        this.charts.enterTypesCounts.setNewData(this.stats.enterTypesCounts.counts);
    }


    private createCharts() {
        const countToRow = (count) => {
            return {c: [{v: count.name}, {v: count.value}]};
        }

        const pairCountToRow = (count) => {
            return {c: [{v: count.name}, {v: count.value.first}, {v: count.value.second}]};
        }

        this.charts = {
            searchTypesCounts: new GoogleChart(
                'PieChart',
                [ {label: '', type: 'string'}, {
                    label: this.localizationService.get(/* @kp-localization commons.pocet */ 'commons.pocet'),
                    type: 'number'
                }],
                {
                    legend: { position: 'right' },
                    chartArea: { width: 350, height: 250, left: 30, top: 50 }
                },
                countToRow),
            movementsCounts: new GoogleChart(
                'BarChart',
                [ {label: '', type: 'string'}, {
                    label: this.localizationService.get(/* @kp-localization commons.pocet */ 'commons.pocet'),
                    type: 'number'
                }],
                {
                    legend: { position: 'none' },
                    chartArea: { width: 400, height: 280, left: 180, top: 0 },
                    hAxis: { textPosition: 'out' }
                },
                countToRow),
            entersAndLoginsCounts: new GoogleChart(
                'ColumnChart',
                [ {label: '', type: 'string'}, {
                    label: this.localizationService.get(/* @kp-localization statistiky.pocetVstupu */ 'statistiky.pocetVstupu'),
                    type: 'number'}, {
                    label: this.localizationService.get(/* @kp-localization statistiky.zTohoPrihlasenych */ 'statistiky.zTohoPrihlasenych'),
                    type: 'number'
                }],
                {
                    chartArea: { width: 850, height: 350, left: 0, top: 0 },
                    hAxis: { textPosition: 'out' },
                    vAxis: { textPosition: 'in' }
                },
                pairCountToRow),
            enterTypesCounts: new GoogleChart(
                'PieChart',
                [ {label: '', type: 'string'}, {label: this.localizationService.get(/* @kp-localization commons.pocet */ 'commons.pocet'), type: 'number'} ],
                {
                    legend: { position: 'none' },
                    chartArea: { width: 350, height: 250, left: 30, top: 50 }
                },
                countToRow)
        };
    }
}

class GoogleChart {
    public readonly chartObject;

    constructor(type, cols, options, private countsToRowsFunction) {
        this.chartObject = {
            type,
            options: angular.extend({
                title: '',
                legend: { position: 'right' },
                chartArea: { width: 350, height: 250, left: 30, top: 50 }
            }, options),
            data: {
                cols,
                rows: []
            }
        };
    }

    public setNewData(counts) {
        this.chartObject.data.rows = counts.map(this.countsToRowsFunction);
    }
}
